// Vendor: manage store nameplate + status + appointments — added to My Store tab
import React, { useCallback, useEffect, useState } from 'react';
import { Palette, Sparkles, Save, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import StoreNameplate, { NAMEPLATE_THEMES, NAMEPLATE_FONTS, NAMEPLATE_VFX } from '../../components/StoreNameplate';
import StoreStatusBadge, { STATUS_OPTIONS } from '../../components/StoreStatusBadge';

export default function StoreCustomizeBlock({ store, onReload }) {
  const [np, setNp] = useState(store.nameplate || { font_family: 'Playfair Display', theme: 'neon', vfx_style: 'glow', glow_color: '#fbbf24', tagline: store.tagline || '' });
  const [status, setStatus] = useState(store.store_status?.value || 'open');
  const [apt, setApt] = useState(store.appointment_config || { slots_per_day: 0, mode: 'physical', price_at: 100, part_payment_percent: 25 });
  const [busy, setBusy] = useState(false);

  const saveNp = async () => {
    setBusy(true);
    try { await api.post('/marketplace/store/nameplate', np); toast.success('✨ Nameplate saved'); onReload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const saveStatus = async (v) => {
    try { await api.post('/marketplace/store/status', { value: v }); setStatus(v); toast.success('✅ Status updated'); onReload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveApt = async () => {
    setBusy(true);
    try { await api.post('/marketplace/store/appointment-config', apt); toast.success('📅 Appointment config saved'); onReload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="store-customize-block">
      {/* Nameplate */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-900/40 to-fuchsia-900/40 border border-fuchsia-400/40 p-4 space-y-3" data-testid="nameplate-editor">
        <div className="text-sm font-black text-fuchsia-200 flex items-center gap-1"><Sparkles size={14} /> Digital Nameplate</div>
        <StoreNameplate nameplate={np} shopName={store.shop_name} tagline={np.tagline} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <label className="text-[10px] font-black opacity-80">Font
            <select value={np.font_family} onChange={(e) => setNp({ ...np, font_family: e.target.value })} data-testid="nameplate-font"
                    className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
              {NAMEPLATE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="text-[10px] font-black opacity-80">Theme
            <select value={np.theme} onChange={(e) => setNp({ ...np, theme: e.target.value })} data-testid="nameplate-theme"
                    className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
              {NAMEPLATE_THEMES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-[10px] font-black opacity-80">VFX
            <select value={np.vfx_style} onChange={(e) => setNp({ ...np, vfx_style: e.target.value })} data-testid="nameplate-vfx"
                    className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
              {NAMEPLATE_VFX.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="text-[10px] font-black opacity-80">Glow Color
            <input type="color" value={np.glow_color || '#fbbf24'} onChange={(e) => setNp({ ...np, glow_color: e.target.value })} data-testid="nameplate-glow"
                   className="w-full mt-0.5 h-8 rounded bg-slate-800 border border-slate-700" />
          </label>
        </div>
        <input value={np.tagline || ''} onChange={(e) => setNp({ ...np, tagline: e.target.value })} placeholder="Tagline (max 200 chars)" maxLength={200} data-testid="nameplate-tagline-input"
               className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        <button onClick={saveNp} disabled={busy} data-testid="nameplate-save"
                className="w-full py-2 rounded-lg bg-fuchsia-500 text-white font-black text-xs flex items-center justify-center gap-1 disabled:opacity-50">
          <Save size={12} /> Save Nameplate
        </button>
      </div>

      {/* Status */}
      <div className="rounded-2xl bg-slate-900/70 border border-cyan-400/40 p-4 space-y-3" data-testid="status-editor">
        <div className="text-sm font-black text-cyan-200 flex items-center gap-1"><Palette size={14} /> Store Status Badge</div>
        <div className="flex justify-center py-2"><StoreStatusBadge status={{ value: status }} size="lg" /></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
          {STATUS_OPTIONS.map((s) => (
            <button key={s.value} onClick={() => saveStatus(s.value)} data-testid={`status-set-${s.value}`}
                    className={`px-2 py-1.5 rounded-lg text-[10px] font-black transition ${status === s.value ? 'bg-cyan-400 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Appointments */}
      <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/40 p-4 space-y-3" data-testid="appointment-config-editor">
        <div className="text-sm font-black text-emerald-200 flex items-center gap-1"><Calendar size={14} /> Appointment Slots</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <label className="text-[10px] font-black opacity-80">Slots/Day
            <input type="number" min="0" max="100" value={apt.slots_per_day} onChange={(e) => setApt({ ...apt, slots_per_day: Number(e.target.value) })} data-testid="apt-slots"
                   className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          </label>
          <label className="text-[10px] font-black opacity-80">Mode
            <select value={apt.mode} onChange={(e) => setApt({ ...apt, mode: e.target.value })} data-testid="apt-mode"
                    className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
              <option value="physical">🏬 Physical Visit</option>
              <option value="virtual">💻 Virtual Video Call</option>
              <option value="both">🔀 Both</option>
            </select>
          </label>
          <label className="text-[10px] font-black opacity-80">Price (AT)
            <input type="number" min="0" value={apt.price_at} onChange={(e) => setApt({ ...apt, price_at: Number(e.target.value) })} data-testid="apt-price"
                   className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          </label>
          <label className="text-[10px] font-black opacity-80">Part Pay %
            <input type="number" min="1" max="100" value={apt.part_payment_percent} onChange={(e) => setApt({ ...apt, part_payment_percent: Number(e.target.value) })} data-testid="apt-part-pay"
                   className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          </label>
        </div>
        <button onClick={saveApt} disabled={busy} data-testid="apt-save"
                className="w-full py-2 rounded-lg bg-emerald-500 text-slate-900 font-black text-xs flex items-center justify-center gap-1 disabled:opacity-50">
          <Save size={12} /> Save Appointment Config
        </button>
        <div className="text-[10px] opacity-70 bg-slate-800/50 p-2 rounded">
          <b>Rules displayed to bookers:</b> Booking amount non-refundable if buyer cancels or no-shows.
          Reschedule allowed only once. Shop-side cancel = auto full refund to buyer's AT wallet.
        </div>
      </div>
    </div>
  );
}
